// src/components/policies/PoliciesList.jsx
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Table from '../common/Table';
import Badge from '../common/Badge';
import { policiesService } from '../../services/policies';

const PoliciesList = ({ filters = {} }) => {
  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchPolicies = async () => {
      try {
        setLoading(true);
        const data = await policiesService.getPolicies(filters);
        setPolicies(data);
        setError(null);
      } catch (err) {
        console.error('Error fetching policies:', err);
        setError('Failed to load security policies. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchPolicies();
  }, [filters]);

  const columns = [
    {
      header: 'Policy Name',
      accessor: 'policy_name',
      render: (policy) => (
        <div className="flex items-center">
          <div className="flex-shrink-0 h-10 w-10 bg-green-100 text-green-600 rounded-full flex items-center justify-center">
            <svg className="h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
          <div className="ml-4">
            <div className="text-sm font-medium text-gray-900">
              {policy.policy_name}
            </div>
            <div className="text-sm text-gray-500">
              ID: {policy.policy_id}
            </div>
          </div>
        </div>
      )
    },
    {
      header: 'Type',
      accessor: 'policy_type',
    },
    {
      header: 'Status',
      accessor: 'is_enabled',
      render: (policy) => (
        <Badge type={policy.is_enabled ? 'online' : 'offline'} text={policy.is_enabled ? 'Enabled' : 'Disabled'} />
      )
    },
    {
      header: 'Scope',
      render: (policy) => (
        <div>
          <div className="text-sm text-gray-900">{policy.scope_type}</div>
          {policy.scope_identifiers && policy.scope_identifiers.length > 0 && (
            <div className="text-xs text-gray-500">
              {policy.scope_identifiers.length} {policy.scope_identifiers.length === 1 ? 'target' : 'targets'}
            </div>
          )}
        </div>
      )
    },
    {
      header: 'Action on Violation',
      accessor: 'action_on_violation',
      render: (policy) => (
        <Badge 
          type={policy.action_on_violation === 'Block' ? 'high' : 'low'} 
          text={policy.action_on_violation || 'AlertOnly'} 
        />
      )
    },
    {
      header: 'Rules',
      render: (policy) => (
        <div className="text-sm text-gray-900">
          {policy.rules && policy.rules.length ? `${policy.rules.length} rules` : 'No rules'}
        </div>
      )
    },
    {
      header: 'Actions',
      render: (policy) => (
        <div className="flex space-x-2">
          <Link
            to={`/policies/${policy.policy_id}`}
            className="text-blue-600 hover:text-blue-900"
          >
            View
          </Link>
          <Link
            to={`/policies/${policy.policy_id}/edit`}
            className="text-green-600 hover:text-green-900"
          >
            Edit
          </Link>
        </div>
      )
    }
  ];

  return (
    <div>
      {error && (
        <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          </div>
        </div>
      )}

      <Table
        columns={columns}
        data={policies}
        isLoading={loading}
        onRowClick={(policy) => window.location.href = `/policies/${policy.policy_id}`}
        emptyMessage="No security policies found. Create a new policy to get started."
      />
    </div>
  );
};

export default PoliciesList;
